import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AdminInput from '../common/AdminInput';
import Button from '../common/Button';
import InputSelect from '../common/InputSelect';
import CheckBoxInput from '../common/checkBox';
import { getTranslation, fetchCorrectTranslation } from '../../utils/translation';

const SignupForm = ({
  teamType = 'team',
  formik,
  regions = [],
  establishments = [],
  academies = [],
  schools = [],
  languageOption,
  getLocalizedOptionName,
  from = 'iframe',
  registrationForm = [],
  submitButtonType = 'primary',
  theme = 'basic'
}) => {
  const { t } = useTranslation();

  const [showOtherSchoolField, setShowOtherSchoolField] = useState(false);

  const isTR2026 = theme === 'tr2026';

  const get = (subKey) => {
    if (isTR2026) {
      const raw = t(`tr_2026.signup.${subKey}`, { returnObjects: true });
      return fetchCorrectTranslation(raw);
    }
    return getTranslation(t, `signup.${subKey}`);
  };
  const labelClass = isTR2026 ? '!text-white [&_*]:!text-white' : 'text-cblack-primary';
  const borderClass = isTR2026 ? 'border-white' : 'border-[#191919]';
  const placeholderClass = isTR2026 ? 'placeholder-white' : '';
  const textClass = isTR2026 ? 'text-white' : 'text-gray-primary';
  const checkboxInputClass = isTR2026 ? 'tr2026-checkbox' : '';
  const errorBaseKey = 'schema.signup';

  const fieldEnabled = (fieldName) => {
    return registrationForm.find((field) => field.id === fieldName)?.enabled;
  };

  return (
    <form onSubmit={formik.handleSubmit} className="signupform w-full mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-1 sm:px-3">
        {/* Organization Type */}
        {fieldEnabled('type') && (
          <InputSelect
            className={`!mb-1 border-2 bg-transparent ${borderClass} ${placeholderClass} w-full`}
            label={get('fields.type')}
            labelClassName={labelClass}
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            placeholder={get('placeholder.type')}
            name="type"
            value={formik.values.type}
            options={establishments.map((establishment) => ({
              value: establishment._id,
              label: getLocalizedOptionName(establishment)
            }))}
            menuPlacement={'top'}
            touched={formik.touched.type}
            error={formik.errors.type}
            onChange={(value) => formik.setFieldValue('type', value)}
          />
        )}
        {/* Organization name */}
        {fieldEnabled('name') && (
          <AdminInput
            label={get('fields.name')}
            labelClassName={labelClass}
            placeholder={get('placeholder.name')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="name"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}

        {/* Org Address */}
        {fieldEnabled('address') && (
          <AdminInput
            label={get('fields.address')}
            labelClassName={labelClass}
            placeholder={get('placeholder.address')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="address"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}
        {/* Org city */}
        {fieldEnabled('city') && (
          <AdminInput
            label={get('fields.city')}
            labelClassName={labelClass}
            placeholder={get('placeholder.city')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="city"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}

        {/* Organization academy */}
        {fieldEnabled('academy') && (
          <InputSelect
            className={`!mb-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            label={get('fields.academy')}
            labelClassName={labelClass}
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            placeholder={get('placeholder.academy')}
            name="academy"
            value={formik.values.academy}
            options={academies.map((academy) => ({
              value: academy._id,
              label: getLocalizedOptionName(academy)
            }))}
            menuPlacement={'top'}
            touched={formik.touched.academy}
            error={formik.errors.academy}
            onChange={(value) => formik.setFieldValue('academy', value)}
          />
        )}
        {/* Organization postal code */}
        {fieldEnabled('postalCode') && (
          <AdminInput
            label={get('fields.postalCode')}
            labelClassName={labelClass}
            placeholder={get('placeholder.postalCode')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="postalCode"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}

        {/* First Name */}
        {fieldEnabled('firstName') && (
          <AdminInput
            label={get('fields.firstName')}
            labelClassName={labelClass}
            placeholder={get('placeholder.firstName')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="firstName"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}
        {/* Last Name */}
        {fieldEnabled('lastName') && (
          <AdminInput
            label={get('fields.lastName')}
            labelClassName={labelClass}
            placeholder={get('placeholder.lastName')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="lastName"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}

        {/* School Organization */}
        {fieldEnabled('schoolOrganization') && teamType === 'school' && (
          <AdminInput
            label={get(`fields.school.${from}`)}
            labelClassName={labelClass}
            placeholder={get('placeholder.school')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="schoolOrganization"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}
        {fieldEnabled('schoolOrganization') && teamType === 'team' && (
          <div>
            <InputSelect
              className={`!mb-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
              label={get(`fields.school.${from}`)}
              labelClassName={labelClass}
              textClassName={textClass}
              errorBaseKey={errorBaseKey}
              placeholder={get('placeholder.school')}
              name="schoolOrganization"
              value={formik.values.schoolOrganization}
              options={schools.map((school) => ({
                value: school._id,
                label: school?.name
              }))}
              menuPlacement={'top'}
              touched={formik.touched.schoolOrganization}
              error={formik.errors.schoolOrganization}
              onChange={(value) => {
                const schoolOrgValue = schools.find((school) => school._id === value);
                formik.setFieldValue('schoolOrganization', schoolOrgValue);
                setShowOtherSchoolField(value === 'other');
              }}
            />

            {showOtherSchoolField && (
              <AdminInput
                placeholder={get('placeholder.schoolOrganizationOther')}
                className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full focus:ring-1 focus:ring-black`}
                name="schoolOrganizationOther"
                textClassName={textClass}
                errorBaseKey={errorBaseKey}
                errorTranslationKey="schoolOrganization"
                defaultMargin={'mb-4'}
                formik={formik}
                focus={false}
              />
            )}
          </div>
        )}
        {/* Function / task in your school / organization */}
        {fieldEnabled('functionTask') && (
          <AdminInput
            label={get('fields.function')}
            labelClassName={labelClass}
            placeholder={get('placeholder.function')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="jsonFunction"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            errorTranslationKey="functionTask"
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}

        {/* Email */}
        {fieldEnabled('email') && (
          <AdminInput
            label={get('fields.email')}
            labelClassName={labelClass}
            placeholder={get('placeholder.email')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="email"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}
        {/* Phone */}
        {fieldEnabled('phone') && (
          <AdminInput
            label={get('fields.phone')}
            labelClassName={labelClass}
            placeholder={get('placeholder.phone')}
            className={`my-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            name="phone"
            type="tel"
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            defaultMargin={'mb-4'}
            formik={formik}
          />
        )}

        {/* Canton */}
        {fieldEnabled('canton') && (
          <InputSelect
            className={`!mb-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            label={get('fields.canton')}
            labelClassName={labelClass}
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            placeholder={get('placeholder.canton')}
            name="canton"
            value={formik.values.canton}
            options={regions.map((region) => ({
              value: region._id,
              label: getLocalizedOptionName(region)
            }))}
            menuPlacement={'top'}
            touched={formik.touched.canton}
            error={formik.errors.canton}
            onChange={(value) => formik.setFieldValue('canton', value)}
          />
        )}
        {/* language */}
        {fieldEnabled('language') && (
          <InputSelect
            className={`!mb-1 signupinput bg-transparent border-2 ${borderClass} ${placeholderClass} rounded-[45px] w-full`}
            label={get('fields.language')}
            labelClassName={labelClass}
            textClassName={textClass}
            errorBaseKey={errorBaseKey}
            placeholder={get('placeholder.language')}
            name="language"
            value={formik.values.language}
            options={languageOption}
            menuPlacement={'top'}
            onChange={(value) => formik.setFieldValue('language', value)}
            touched={formik.touched.language}
            error={formik.errors.language}
          />
        )}

        {/* isTeacher */}
        {fieldEnabled('isTeacher') && (
          <CheckBoxInput
            label={get('fields.isTeacher')}
            labelClassName={labelClass}
            inputClassName={checkboxInputClass}
            errorBaseKey={errorBaseKey}
            className="my-1 flex w-full"
            type="checkbox"
            name="isTeacher"
            value={formik.values.isTeacher}
            touched={formik.touched.isTeacher}
            onChange={formik.handleChange}
            fieldValue={formik.values.isTeacher}
            onBlur={formik.handleBlur}
            checked={formik.values.isTeacher}
            error={formik.errors.isTeacher}
          />
        )}

        {/* wantAccess */}
        {fieldEnabled('wantAccess') && (
          <CheckBoxInput
            label={get('fields.wantAccess')}
            labelClassName={labelClass}
            inputClassName={checkboxInputClass}
            errorBaseKey={errorBaseKey}
            className="my-1 flex w-full"
            type="checkbox"
            name="wantAccess"
            fieldValue={formik.values.wantAccess}
            value={formik.values.wantAccess}
            touched={formik.touched.wantAccess}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            checked={formik.values.wantAccess}
            error={formik.errors.wantAccess}
          />
        )}
      </div>

      <div className={`mt-6 mb-2 flex ${isTR2026 ? 'justify-center' : 'justify-center mx-auto'}`}>
        <Button
          type="submit"
          buttonType={isTR2026 ? 'tr2026' : submitButtonType}
          loading={formik.isSubmitting}
          loadingColor={'#fff'}
          // disabled={formik.isSubmitting || Object.keys(formik.errors)?.length > 0}
          data-testid="submit-button">
          {get('button')}
        </Button>
      </div>
    </form>
  );
};
export default SignupForm;
